import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useAuth, canCrawlSignals } from "@/lib/auth";
import { formatLakh, formatDate } from "@/lib/utils-ros";
import {
  RotateCcw, ShieldCheck, Zap, Leaf, Cpu, Wrench, CheckCircle2, Package, Landmark,
  ChevronDown, ChevronRight, Target, Loader2,
} from "lucide-react";
import { PageToolbar } from "@/components/revenueos/PageToolbar";
import { toast } from "sonner";

const MODULES = [
  { key: "safety", label: "Safety", icon: ShieldCheck, color: "#F59E0B" },
  { key: "energy", label: "Energy", icon: Zap, color: "#84CC16" },
  { key: "sustainability", label: "Sustainability", icon: Leaf, color: "#047857" },
  { key: "digital", label: "Digital MRV", icon: Cpu, color: "#0B84F3" },
  { key: "operations", label: "Operations", icon: Wrench, color: "#7E22CE" },
  { key: "quality", label: "Quality", icon: CheckCircle2, color: "#0891B2" },
  { key: "carbon", label: "Carbon", icon: Package, color: "#3F3F46" },
  { key: "governance", label: "Governance", icon: Landmark, color: "#07539A" },
];

const TYPE_COLOR = {
  annual_renewal: "var(--copper)", expansion: "#10B981", upsell: "#F59E0B",
  cross_sell: "#8B5CF6", consumables: "#3B82F6",
};

function ModuleMatrix({ profile }) {
  const boughtMap = Object.fromEntries((profile.modules_bought || []).map((m) => [m.module, m]));
  const funnelMap = Object.fromEntries((profile.modules_in_funnel || []).map((m) => [m.module, m]));
  const unsoldSet = new Set(profile.modules_unsold_keys || []);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-3" data-testid={`module-matrix-${profile.company_id}`}>
      {MODULES.map((mod) => {
        const bought = boughtMap[mod.key];
        const inFunnel = funnelMap[mod.key];
        const unsold = unsoldSet.has(mod.key);
        const Icon = mod.icon;
        let state = "empty";
        if (bought) state = "bought";
        else if (inFunnel) state = "funnel";
        else if (unsold) state = "unsold";

        return (
          <div
            key={mod.key}
            data-testid={`module-cell-${profile.company_id}-${mod.key}`}
            className={`rounded-2xl border border-white/20 p-3 min-h-[5.5rem] flex flex-col !text-white shadow-[0_7px_16px_rgba(10,42,67,0.12)] ${state === "empty" ? "opacity-40" : "opacity-100"}`}
            style={{ backgroundColor: mod.color }}
          >
            <div className="flex items-center gap-1.5 mb-1">
              <span className="w-7 h-7 rounded-lg bg-white/15 flex items-center justify-center"><Icon size={14} className="!text-white" /></span>
              <span className="text-[11px] font-semibold !text-white">{mod.label}</span>
            </div>
            {bought && (
              <>
                <span className="text-[9px] uppercase tracking-wider !text-white/80">Bought</span>
                <span className="text-[11px] font-mono tabular-nums mt-0.5 !text-white">{formatLakh(bought.value_cr)}</span>
              </>
            )}
            {inFunnel && !bought && (
              <>
                <span className="text-[9px] uppercase tracking-wider !text-white/80">In funnel</span>
                <span className="text-[11px] font-mono tabular-nums mt-0.5 !text-white">{formatLakh(inFunnel.value_cr)}</span>
              </>
            )}
            {unsold && !bought && !inFunnel && (
              <span className="text-[10px] leading-snug mt-0.5 !text-white/90">Not tested · expand</span>
            )}
            {!bought && !inFunnel && !unsold && (
              <span className="text-[10px] !text-white/70 mt-0.5">—</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CustomerExpansionCard({ profile, expanded, onToggle }) {
  const expansionCount = profile.expansion_count ?? profile.modules_unsold?.length ?? 0;
  const prompts = profile.active_prompts || [];

  return (
    <div
      className="border border-[#C9DCE6] rounded-[22px] bg-white overflow-hidden shadow-[0_8px_22px_rgba(7,83,154,0.06)]"
      data-testid={`expansion-${profile.company_id}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left p-4 md:p-5 hover:bg-[#F8FCFE] transition-colors"
        data-testid={`expansion-toggle-${profile.company_id}`}
      >
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span className="font-display text-[17px] text-[#12324A]">{profile.company_name}</span>
              {profile.status === "active_customer" && (
                <span className="text-[9px] font-semibold px-2.5 py-1 rounded-full bg-[#10B981]/10 text-[#047857] border border-[#10B981]/20">
                  Customer
                </span>
              )}
              {expansionCount > 0 && (
                <span className="text-[9px] font-semibold px-2.5 py-1 rounded-full bg-[#FFF3E2] text-[#C76500] border border-[#F0C88E]">
                  {expansionCount} module{expansionCount !== 1 ? "s" : ""} to expand
                </span>
              )}
              {prompts.length > 0 && (
                <span className="text-[9px] font-semibold px-2.5 py-1 rounded-full bg-[#E7F5FB] text-[#07539A] border border-[#C9DCE6]">
                  {prompts.length} active prompt{prompts.length !== 1 ? "s" : ""}
                </span>
              )}
            </div>
            <p className="text-[12px] text-[#607D8B] leading-relaxed" data-testid={`expansion-summary-${profile.company_id}`}>
              {profile.expansion_summary}
            </p>
          </div>
          <div className="shrink-0 text-right">
            <div className="text-[9px] uppercase tracking-widest text-[#78909C]">Deployed</div>
            <div className="font-display text-lg tabular-nums text-[#07539A]">{formatLakh(profile.total_bought_value_cr)}</div>
            {expanded ? <ChevronDown size={14} className="ml-auto mt-2 text-[#07539A]" /> : <ChevronRight size={14} className="ml-auto mt-2 text-[#78909C]" />}
          </div>
        </div>
      </button>
      {expanded && (
        <div className="px-4 md:px-5 pb-4 md:pb-5 border-t border-[#D7E8F0] bg-[#F8FCFE] pt-3 space-y-3">
          <ModuleMatrix profile={profile} />
          {prompts.length > 0 && (
            <div className="border border-[#C9DCE6] rounded-2xl bg-[#EAF6FB] p-3.5" data-testid={`expansion-prompts-${profile.company_id}`}>
              <div className="text-[11px] font-semibold text-[#07539A] mb-2">Partner prompts</div>
              <div className="space-y-2">
                {prompts.map((t) => (
                  <div key={t.id} className="text-[12px] bg-white border border-[#D7E8F0] rounded-xl p-3">
                    <div className="text-[#243B53]">{t.title}</div>
                    <div className="text-[10px] text-[#78909C] mt-1">Due {formatDate(t.due_date)} · assigned to partner</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Reorder() {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [reorders, setReorders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const canSync = canCrawlSignals(user);

  async function load() {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([api.get("/land-expand"), api.get("/reorders")]);
      setProfiles(p.data);
      setReorders(r.data);
      if (p.data.length && !expandedId) setExpandedId(p.data[0].company_id);
    } catch {
      toast.error("Could not load reorder data");
      setProfiles([]);
      setReorders([]);
    } finally {
      setLoading(false);
    }
  }

  async function syncPrompts() {
    setSyncing(true);
    try {
      await api.post("/admin/expansion-prompts/sync", { force: true });
      toast.success("Expansion prompt sync started");
      window.setTimeout(() => load(), 2000);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  }

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return profiles;
    return profiles.filter((p) => {
      const hay = [
        p.company_name,
        p.industry,
        p.expansion_summary,
        ...(p.modules_bought || []).map((m) => m.label),
        ...(p.modules_unsold || []).map((m) => m.label),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [profiles, query]);

  const filteredReorders = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return reorders;
    return reorders.filter((r) =>
      [r.company?.name, r.renewal_type, r.ai_reason, r.expansion_summary, ...(r.modules_unsold || [])]
        .filter(Boolean).join(" ").toLowerCase().includes(q),
    );
  }, [reorders, query]);

  const totalExpansion = filtered.reduce((s, p) => s + (p.expansion_count || 0), 0);
  const totalDeployed = filtered.reduce((s, p) => s + (p.total_bought_value_cr || 0), 0);

  return (
    <div className="min-h-full bg-[#F7FAFC] pb-12" data-testid="reorder-page">
      <section className="bg-[#07539A] px-5 md:px-9 pt-6 pb-16 shadow-[inset_0_-1px_0_rgba(255,255,255,0.12)]">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
          <div>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight text-[#FFFFFF]">Reorder</h1>
            <p className="text-[13px] text-white/75 mt-1 max-w-2xl">See what each customer has deployed, identify untested Ergoniq modules, and manage renewal opportunities.</p>
          </div>
          <div className="flex flex-col items-start lg:items-end gap-3">
          <div className="flex gap-8 text-left lg:text-right">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/60">Deployed</div>
            <div className="font-display text-2xl tabular-nums !text-white">{formatLakh(totalDeployed)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/60">Expansion targets</div>
            <div className="font-display text-2xl tabular-nums !text-[#F6A63B]">{totalExpansion}</div>
          </div>
        </div>
        </div>
        </div>
      </section>

      <div className="px-4 md:px-8 -mt-7 relative z-10 space-y-7">
        <PageToolbar
          query={query}
          onQueryChange={setQuery}
          placeholder="Search customers by company, module, or industry…"
          entityLabel="customers"
          count={filtered.length}
          variant="universe"
          afterPrompts={canSync ? (
            <button
              type="button"
              onClick={syncPrompts}
              disabled={syncing}
              data-testid="expansion-sync-btn"
              className="shrink-0 h-9 px-3.5 rounded-full border border-[#BFD6E1] bg-white text-[#07539A] hover:border-[#07539A] hover:bg-[#F3FAFD] text-[12px] font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-50 shadow-[0_5px_14px_rgba(7,83,154,0.08)]"
            >
              {syncing ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
              Sync partner prompts
            </button>
          ) : null}
        />

      {loading ? (
        <div className="bg-white border border-[#C9DCE6] rounded-[22px] text-[#78909C] p-10 text-center shadow-[0_10px_28px_rgba(7,83,154,0.08)]">Loading expansion intelligence…</div>
      ) : (
        <>
          <section data-testid="land-expand-section">
            <div className="flex items-center gap-2.5 mb-3">
              <span className="w-8 h-8 rounded-lg bg-[#E7F5FB] text-[#07539A] flex items-center justify-center"><Target size={14} /></span>
              <h2 className="font-display text-lg text-[#12324A]">Customer module footprint</h2>
            </div>
            {filtered.length === 0 ? (
              <div className="text-center p-10 border border-dashed border-[#B8D3DF] bg-white rounded-[22px]">
                <span className="w-11 h-11 rounded-xl bg-[#E7F5FB] text-[#07539A] flex items-center justify-center mx-auto mb-3"><Package size={21} /></span>
                <div className="text-[13px] text-[#78909C]">
                  {profiles.length === 0
                    ? "No customer module data yet. Book modules from an opportunity to populate this view."
                    : "No customers match your search."}
                </div>
              </div>
            ) : (
              <div className="grid gap-3">
                {filtered.map((p) => (
                  <CustomerExpansionCard
                    key={p.company_id}
                    profile={p}
                    expanded={expandedId === p.company_id}
                    onToggle={() => setExpandedId(expandedId === p.company_id ? null : p.company_id)}
                  />
                ))}
              </div>
            )}
          </section>

          <section data-testid="renewal-pipeline-section">
            <div className="flex items-center gap-2.5 mb-3">
              <span className="w-8 h-8 rounded-lg bg-[#E7F5FB] text-[#07539A] flex items-center justify-center"><RotateCcw size={14} /></span>
              <h2 className="font-display text-lg text-[#12324A]">Renewal pipeline</h2>
              <span className="min-w-6 h-6 px-2 rounded-full bg-[#E7F5FB] text-[#607D8B] text-[10px] flex items-center justify-center">{filteredReorders.length}</span>
            </div>
            {filteredReorders.length === 0 ? (
              <div className="text-[12px] text-[#78909C] p-6 border border-[#C9DCE6] rounded-[22px] bg-white text-center shadow-[0_6px_18px_rgba(7,83,154,0.05)]">
                No renewal opportunities match your search.
              </div>
            ) : (
              <div className="grid gap-3">
                {filteredReorders.map((r) => (
                  <div key={r.id} data-testid={`reorder-${r.id}`} className="border border-[#C9DCE6] rounded-[22px] bg-white p-4 md:p-5 shadow-[0_8px_22px_rgba(7,83,154,0.06)]">
                    <div className="flex flex-col md:flex-row md:items-start gap-3 md:gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="text-[9px] font-semibold px-2.5 py-1 rounded-full border capitalize" style={{
                            color: TYPE_COLOR[r.renewal_type], borderColor: `${TYPE_COLOR[r.renewal_type]}40`, background: `${TYPE_COLOR[r.renewal_type]}10`,
                          }}>{r.renewal_type.replace("_", " ")}</span>
                          <span className="text-[11px] text-[#78909C]">Due {formatDate(r.due_date)}</span>
                        </div>
                        <div className="font-display text-[16px] text-[#12324A]">{r.company?.name}</div>
                        <div className="text-[12px] text-[#607D8B] mt-2 leading-relaxed">{r.ai_reason}</div>
                        {r.expansion_summary && (
                          <div className="text-[11px] text-[#07539A] mt-2 leading-relaxed border-l-2 border-[#9FC8DB] pl-2">
                            {r.expansion_summary}
                          </div>
                        )}
                        {(r.modules_bought?.length > 0 || r.modules_unsold?.length > 0) && (
                          <div className="mt-3 flex flex-wrap gap-2 text-[10px]">
                            {(r.modules_bought || []).map((m) => (
                              <span key={`b-${m}`} className="px-2.5 py-1 rounded-full bg-[#10B981]/10 text-[#047857] border border-[#10B981]/20">
                                {m} · bought
                              </span>
                            ))}
                            {(r.modules_unsold || []).map((m) => (
                              <span key={`u-${m}`} className="px-2.5 py-1 rounded-full bg-[#FFF3E2] text-[#C76500] border border-[#F0C88E]">
                                {m} · not tested
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex md:flex-col md:items-end gap-4 md:gap-2 md:text-right shrink-0">
                        <div>
                          <div className="text-[9px] uppercase tracking-widest text-[#78909C]">Value</div>
                          <div className="font-display text-lg tabular-nums text-[#12324A]">{formatLakh(r.expected_value_cr)}</div>
                        </div>
                        <div>
                          <div className="text-[9px] uppercase tracking-widest text-[#78909C]">Probability</div>
                          <div className="font-mono text-[13px] text-[#E58D22] tabular-nums">{r.probability}%</div>
                        </div>
                        <div>
                          <div className="text-[9px] uppercase tracking-widest text-[#78909C]">Status</div>
                          <div className="text-[11px] capitalize text-[#455A64]">{r.status.replace("_", " ")}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
      </div>
    </div>
  );
}
